import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const EMPTY = { name: "", brand: "", city: "", state: "", phone: "", address: "", services: "", hours: "" };

export default function AdminServiceCentres() {
  const [centres, setCentres] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCentres = useCallback(async () => {
    try { const r = await api.get("/service-centres", { params: { state: "all" } }); setCentres(r.data.centres); }
    catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim() || !form.city.trim() || !form.state.trim() || !form.phone.trim()) {
      toast.error("Name, city, state and phone are required."); return;
    }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/service-centres/${editingId}`, form); toast.success("Centre updated"); }
      else { await api.post("/service-centres", form); toast.success("Centre added"); }
      reset(); await fetchCentres();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save centre"); }
    finally { setSaving(false); }
  };

  const edit = (c) => { setForm({ name: c.name, brand: c.brand || "", city: c.city, state: c.state, phone: c.phone, address: c.address || "", services: c.services || "", hours: c.hours || "" }); setEditingId(c.id); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/service-centres/${c.id}`); toast.success("Centre deleted"); await fetchCentres(); if (editingId === c.id) reset(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border font-heading font-semibold flex items-center gap-2">
        <Wrench className="w-4 h-4 text-primary" /> Repair Centres ({centres.length})
      </div>

      <div className="p-5 border-b border-border grid sm:grid-cols-2 gap-3">
        <Input data-testid="centre-name-input" placeholder="Centre name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <Input data-testid="centre-brand-input" placeholder="Brand (Carrier / Thermo King)" value={form.brand} onChange={(e) => set("brand", e.target.value)} />
        <Input data-testid="centre-phone-input" placeholder="Phone (e.g. +9198XXXXXXXX)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        <Input data-testid="centre-city-input" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} />
        <Input data-testid="centre-state-input" placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} />
        <Input data-testid="centre-address-input" className="sm:col-span-2" placeholder="Address" value={form.address} onChange={(e) => set("address", e.target.value)} />
        <Input data-testid="centre-services-input" className="sm:col-span-2" placeholder="Services offered" value={form.services} onChange={(e) => set("services", e.target.value)} />
        <Input data-testid="centre-hours-input" placeholder="Hours (e.g. Mon–Sat, 9am–8pm)" value={form.hours} onChange={(e) => set("hours", e.target.value)} />
        <div className="flex items-center gap-2">
          <Button data-testid="centre-save-btn" onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {editingId ? <><Pencil className="w-4 h-4 mr-1.5" />Update</> : <><Plus className="w-4 h-4 mr-1.5" />Add centre</>}
          </Button>
          {editingId && <Button data-testid="centre-cancel-btn" onClick={reset} variant="secondary" className="rounded-full"><X className="w-4 h-4 mr-1.5" />Cancel</Button>}
        </div>
      </div>

      <div className="divide-y divide-border/60">
        {centres.map((c) => (
          <div key={c.id} data-testid="admin-centre-row" className="flex items-center gap-3 px-5 py-3.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{c.name} <span className="text-muted-foreground font-normal">· {c.city}, {c.state}</span></p>
              <p className="text-xs text-muted-foreground truncate">{c.phone}{c.services ? ` · ${c.services}` : ""}</p>
            </div>
            <Button data-testid="centre-edit-btn" size="sm" variant="outline" onClick={() => edit(c)} className="rounded-full h-7 text-xs shrink-0"><Pencil className="w-3.5 h-3.5" /></Button>
            <Button data-testid="centre-delete-btn" size="sm" variant="outline" onClick={() => remove(c)} className="rounded-full h-7 text-xs shrink-0 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        {centres.length === 0 && <div className="px-5 py-8 text-center text-sm text-muted-foreground">No centres yet. Add one above.</div>}
      </div>
    </div>
  );
}
